/**
 * SLAWarningToast — displays SLA warning notifications as toast messages.
 *
 * Shows motivational, context-aware messages for files approaching
 * or exceeding the 3-day SLA threshold. Auto-dismisses after a timeout.
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Clock, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { getSLAMotivationalMessage } from '../../api/slaMonitor';
import { calculateActiveDays, getSLAUrgency } from '../../utils/slaCalculator';

/**
 * @param {Array} files — list of active files to check for SLA warnings
 * @param {number} dismissAfterMs — auto-dismiss duration (default 15s)
 */
export default function SLAWarningToast({ files = [], dismissAfterMs = 15000 }) {
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const navigate = useNavigate();
  const { role } = useAuth();

  // Working file folders are the Administration Assistant's responsibility,
  // so these "needs attention" toasts fire only for that active role.
  // Adviser and New Business Assistant see nothing — no toast state is
  // created at all. Role comes from the auth context, so a role switch
  // re-evaluates this gate immediately.
  if (role !== 'assistant') return null;

  // Find files that need SLA warnings (approaching or breached)
  const warningFiles = files.filter((f) => {
    if (dismissedIds.has(f.id)) return false;
    // Test records never raise SLA warnings (CLAUDE.md §33).
    if (f.isTest === true) return false;
    if (!['in_progress', 'paused'].includes(f.status)) return false;
    const days = calculateActiveDays(f);
    return days >= 2; // Show warnings from 2 days onwards
  });

  const dismiss = (fileId) => {
    setDismissedIds((prev) => new Set([...prev, fileId]));
  };

  return (
    <div style={styles.container}>
      <AnimatePresence>
        {warningFiles.map((file) => (
          <SLAToastItem
            key={file.id}
            file={file}
            onDismiss={() => dismiss(file.id)}
            onNavigate={() => navigate(`/file/${file.id}`)}
            dismissAfterMs={dismissAfterMs}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

function SLAToastItem({ file, onDismiss, onNavigate, dismissAfterMs }) {
  const urgency = getSLAUrgency(file);
  const days = calculateActiveDays(file);
  const message = getSLAMotivationalMessage(file);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const colors = {
    green: { bg: '#E6F7F5', border: 'var(--cp-success)', icon: '#2EC4B6' },
    amber: { bg: '#FEF3E2', border: 'var(--cp-accent)', icon: '#F4A261' },
    red: { bg: '#FDEDED', border: '#C0392B', icon: '#C0392B' },
  };

  const palette = colors[urgency];

  // Auto-dismiss
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onDismissRef.current) onDismissRef.current();
    }, dismissAfterMs);
    return () => clearTimeout(timer);
  }, [dismissAfterMs]);

  if (!message) return null;

  return (
    <motion.div
      style={{
        ...styles.toast,
        background: palette.bg,
        borderLeft: `4px solid ${palette.border}`,
      }}
      initial={{ opacity: 0, x: 60, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.95 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <div style={styles.toastIcon}>
        {urgency === 'red' ? (
          <AlertTriangle size={18} color={palette.icon} />
        ) : (
          <Clock size={18} color={palette.icon} />
        )}
      </div>

      <div style={styles.toastContent}>
        <div style={styles.toastHeader}>
          <span style={{ ...styles.toastClient, color: palette.icon }}>
            {file.clientName}
          </span>
          <span style={styles.toastDays}>{days.toFixed(1)} days</span>
        </div>
        <p style={styles.toastMessage}>{message}</p>
        <button style={styles.toastAction} onClick={onNavigate}>
          Open file <ChevronRight size={12} />
        </button>
      </div>

      <button style={styles.toastClose} onClick={onDismiss}>
        <X size={14} />
      </button>
    </motion.div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1500,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    maxWidth: '380px',
    width: '100%',
  },
  toast: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.65rem',
    padding: '0.85rem 1rem',
    borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    cursor: 'default',
    userSelect: 'none',
  },
  toastIcon: {
    paddingTop: '2px',
    flexShrink: 0,
  },
  toastContent: {
    flex: 1,
    minWidth: 0,
  },
  toastHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.2rem',
  },
  toastClient: {
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  toastDays: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#6B7A8D',
  },
  toastMessage: {
    fontSize: '0.8rem',
    color: 'var(--cp-text)',
    lineHeight: 1.4,
    margin: 0,
  },
  toastAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    marginTop: '0.35rem',
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#0F4C75',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  toastClose: {
    padding: '2px',
    border: 'none',
    background: 'none',
    color: '#6B7A8D',
    cursor: 'pointer',
    flexShrink: 0,
    opacity: 0.6,
  },
};
